import React, { useEffect, useState } from 'react';
import { backofficeDb } from '../backofficeFirebaseClient';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ensureAppServiceSignIn, getAppFirestore } from '../appFirebaseClient';

const Input = (p) => (<input {...p} className={(p.className||'') + ' border rounded px-3 py-2 w-full'} />);
const Textarea = (p) => (<textarea {...p} className={(p.className||'') + ' border rounded px-3 py-2 w-full'} />);

const PublicContentManager = () => {
  const [active, setActive] = useState('hero');
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [content, setContent] = useState({
    hero: { heading: '', description: '', bannerUrl: '' },
    pricing: { plans: [{name:'Basic', price: 999, features:['Invoices','GST'], cta:'Start Free'}] },
    features: [{title:'Sales & Invoicing', description:'Invoice, Quotation, Challan', icon:'document-text'}],
    testimonials: [{name:'Priya Sharma', company:'Sharma Enterprises', text:'Loved it!'}],
    faqs: [{q:'What is ACCTOO?', a:'Cloud accounting for Indian businesses.'}],
    contact: { email:'hello@acctoo.com', phone:'+91-00000 00000', address:'India' }
  });

  useEffect(() => {
    const load = async () => {
      const ref = doc(backofficeDb, 'cms_public', 'content');
      const snap = await getDoc(ref);
      if (snap.exists()) setContent({ ...content, ...(snap.data() || {}) });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    load();
  }, []);

  const save = async () => {
    setLoading(true);
    setSaveMessage('');
    try {
      const ref = doc(backofficeDb, 'cms_public', 'content');
      await setDoc(ref, { ...content, updatedAt: serverTimestamp() }, { merge: true });

      await ensureAppServiceSignIn();
      const appDb = getAppFirestore();
      const mirrorRef = doc(appDb, 'publicConfig', 'cms_mirror');
      await setDoc(
        mirrorRef,
        {
          source: 'cms_public',
          content,
          publishedAt: serverTimestamp()
        },
        { merge: true }
      );
      setSaveMessage('Saved to CMS and published mirror (publicConfig/cms_mirror) for the app project.');
    } catch (e) {
      setSaveMessage(e.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="text-xl font-semibold">Public Content Manager</div>
      {saveMessage && (
        <div className={`text-sm rounded border px-3 py-2 ${saveMessage.startsWith('Saved') ? 'bg-green-50 text-green-900 border-green-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
          {saveMessage}
        </div>
      )}
      <div className="flex gap-2 border-b pb-2">
        {['hero','pricing','features','testimonials','faqs','contact','cta','footer'].map(t => (
          <button key={t} className={`px-3 py-2 rounded-t ${active===t?'bg-blue-100 border border-b-white border-blue-300':'bg-white border'}`} onClick={()=>setActive(t)}>
            {t[0].toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>

      {active==='hero' && (
        <div className="bg-white border rounded p-4 space-y-3">
          <label className="flex flex-col">
            <span className="text-sm font-medium mb-1">Heading</span>
            <Input value={content.hero.heading} onChange={(e)=>setContent({...content, hero:{...content.hero, heading:e.target.value}})} />
          </label>
          <label className="flex flex-col">
            <span className="text-sm font-medium mb-1">Description</span>
            <Textarea rows={2} value={content.hero.description} onChange={(e)=>setContent({...content, hero:{...content.hero, description:e.target.value}})} />
          </label>
          <label className="flex flex-col">
            <span className="text-sm font-medium mb-1">Banner URL</span>
            <Input value={content.hero.bannerUrl} onChange={(e)=>setContent({...content, hero:{...content.hero, bannerUrl:e.target.value}})} />
          </label>
        </div>
      )}

      {active==='pricing' && (
        <div className="bg-white border rounded p-4 space-y-3">
          <div className="font-medium">Plans</div>
          {content.pricing.plans.map((p, i) => (
            <div key={i} className="border rounded p-3 space-y-2">
              <div className="grid sm:grid-cols-3 gap-2">
                <Input value={p.name} onChange={(e)=>{
                  const plans=[...content.pricing.plans]; plans[i]={...plans[i], name:e.target.value}; setContent({...content, pricing:{...content.pricing, plans}});
                }} placeholder="Plan name" />
                <Input type="number" value={p.price} onChange={(e)=>{
                  const plans=[...content.pricing.plans]; plans[i]={...plans[i], price:Number(e.target.value)}; setContent({...content, pricing:{...content.pricing, plans}});
                }} placeholder="Price" />
                <Input value={p.cta} onChange={(e)=>{
                  const plans=[...content.pricing.plans]; plans[i]={...plans[i], cta:e.target.value}; setContent({...content, pricing:{...content.pricing, plans}});
                }} placeholder="CTA Text" />
              </div>
              <label className="flex flex-col">
                <span className="text-sm font-medium mb-1">Features (comma separated)</span>
                <Input value={(p.features||[]).join(', ')} onChange={(e)=>{
                  const plans=[...content.pricing.plans]; plans[i]={...plans[i], features:e.target.value.split(',').map(s=>s.trim())}; setContent({...content, pricing:{...content.pricing, plans}});
                }} />
              </label>
            </div>
          ))}
          <div className="flex gap-2">
            <button className="px-3 py-2 border rounded" onClick={()=>setContent({...content, pricing:{...content.pricing, plans:[...content.pricing.plans, {name:'New', price:0, features:[], cta:'Buy'}]}})}>+ Add Plan</button>
            <button className="px-4 py-2 bg-green-600 text-white rounded" onClick={save} disabled={loading}>{loading?'Saving...':'Save'}</button>
          </div>
        </div>
      )}

      {active==='features' && (
        <div className="bg-white border rounded p-4 space-y-3">
          <div className="font-medium">Features</div>
          {content.features.map((f,i)=>(
            <div key={i} className="border rounded p-3 grid sm:grid-cols-3 gap-2">
              <Input value={f.title} onChange={(e)=>{const arr=[...content.features]; arr[i]={...arr[i], title:e.target.value}; setContent({...content, features:arr});}} placeholder="Title" />
              <Input value={f.icon} onChange={(e)=>{const arr=[...content.features]; arr[i]={...arr[i], icon:e.target.value}; setContent({...content, features:arr});}} placeholder="Icon" />
              <Textarea rows={1} value={f.description} onChange={(e)=>{const arr=[...content.features]; arr[i]={...arr[i], description:e.target.value}; setContent({...content, features:arr});}} placeholder="Description" />
            </div>
          ))}
          <button className="px-3 py-2 border rounded" onClick={()=>setContent({...content, features:[...content.features, {title:'New Feature', description:'...', icon:'star'}]})}>+ Add Feature</button>
        </div>
      )}

      {active==='testimonials' && (
        <div className="bg-white border rounded p-4 space-y-3">
          <div className="font-medium">Testimonials</div>
          {content.testimonials.map((t,i)=>(
            <div key={i} className="border rounded p-3 grid sm:grid-cols-3 gap-2">
              <Input value={t.name} onChange={(e)=>{const arr=[...content.testimonials]; arr[i]={...arr[i], name:e.target.value}; setContent({...content, testimonials:arr});}} placeholder="Name" />
              <Input value={t.company} onChange={(e)=>{const arr=[...content.testimonials]; arr[i]={...arr[i], company:e.target.value}; setContent({...content, testimonials:arr});}} placeholder="Company" />
              <Textarea rows={1} value={t.text} onChange={(e)=>{const arr=[...content.testimonials]; arr[i]={...arr[i], text:e.target.value}; setContent({...content, testimonials:arr});}} placeholder="Testimonial" />
            </div>
          ))}
          <button className="px-3 py-2 border rounded" onClick={()=>setContent({...content, testimonials:[...content.testimonials, {name:'New', company:'', text:'Great!'}]})}>+ Add Testimonial</button>
        </div>
      )}

      {active==='faqs' && (
        <div className="bg-white border rounded p-4 space-y-3">
          <div className="font-medium">FAQs</div>
          {content.faqs.map((f,i)=>(
            <div key={i} className="border rounded p-3 grid gap-2">
              <Input value={f.q} onChange={(e)=>{const arr=[...content.faqs]; arr[i]={...arr[i], q:e.target.value}; setContent({...content, faqs:arr});}} placeholder="Question" />
              <Textarea rows={2} value={f.a} onChange={(e)=>{const arr=[...content.faqs]; arr[i]={...arr[i], a:e.target.value}; setContent({...content, faqs:arr});}} placeholder="Answer" />
            </div>
          ))}
          <button className="px-3 py-2 border rounded" onClick={()=>setContent({...content, faqs:[...content.faqs, {q:'New question?', a:'New answer'}]})}>+ Add FAQ</button>
        </div>
      )}

      {active==='contact' && (
        <div className="bg-white border rounded p-4 space-y-3">
          <label className="flex flex-col"><span className="text-sm font-medium mb-1">Email</span><Input value={content.contact.email} onChange={(e)=>setContent({...content, contact:{...content.contact, email:e.target.value}})} /></label>
          <label className="flex flex-col"><span className="text-sm font-medium mb-1">Phone</span><Input value={content.contact.phone} onChange={(e)=>setContent({...content, contact:{...content.contact, phone:e.target.value}})} /></label>
          <label className="flex flex-col"><span className="text-sm font-medium mb-1">Address</span><Textarea rows={2} value={content.contact.address} onChange={(e)=>setContent({...content, contact:{...content.contact, address:e.target.value}})} /></label>
        </div>
      )}

      {active==='cta' && (
        <div className="bg-white border rounded p-4 space-y-3">
          <div className="font-medium">Call To Action</div>
          <label className="flex flex-col"><span className="text-sm font-medium mb-1">Heading</span><Input value={content.cta?.heading||''} onChange={(e)=>setContent({...content, cta:{...(content.cta||{}), heading:e.target.value}})} /></label>
          <label className="flex flex-col"><span className="text-sm font-medium mb-1">Subheading</span><Input value={content.cta?.subheading||''} onChange={(e)=>setContent({...content, cta:{...(content.cta||{}), subheading:e.target.value}})} /></label>
          <div className="grid sm:grid-cols-2 gap-2">
            <label className="flex flex-col"><span className="text-sm font-medium mb-1">Primary CTA</span><Input value={content.cta?.primaryCtaText||''} onChange={(e)=>setContent({...content, cta:{...(content.cta||{}), primaryCtaText:e.target.value}})} /></label>
            <label className="flex flex-col"><span className="text-sm font-medium mb-1">Secondary CTA</span><Input value={content.cta?.secondaryCtaText||''} onChange={(e)=>setContent({...content, cta:{...(content.cta||{}), secondaryCtaText:e.target.value}})} /></label>
          </div>
        </div>
      )}

      {active==='footer' && (
        <div className="bg-white border rounded p-4 space-y-3">
          <div className="font-medium">Footer</div>
          <label className="flex flex-col"><span className="text-sm font-medium mb-1">About</span><Textarea rows={2} value={content.footer?.about||''} onChange={(e)=>setContent({...content, footer:{...(content.footer||{}), about:e.target.value}})} /></label>
          <label className="flex flex-col"><span className="text-sm font-medium mb-1">Keywords (comma separated)</span><Input value={(content.footer?.keywords||[]).join(', ')} onChange={(e)=>setContent({...content, footer:{...(content.footer||{}), keywords:e.target.value.split(',').map(s=>s.trim())}})} /></label>
        </div>
      )}

      <div className="flex justify-end"><button className="px-4 py-2 bg-green-600 text-white rounded" onClick={save} disabled={loading}>{loading?'Saving...':'Save All'}</button></div>
    </div>
  );
};

export default PublicContentManager;


